import React from "react";
import * as PropTypes from "prop-types";
import { css } from "@emotion/css";
import { maximumYear, minimumYear } from "../../constants";
import classNames from "classnames";
import NativeSelect from "@mui/material/NativeSelect";
import TextField from "@mui/material/TextField";
import MaritalStatusSelector from "../SentenceMaritalStatusSelector";
import { deltaPropType, directionPropType, maritalStatusPropType } from "../../propTypes";

const classes = {
    container: css`
        display: inline-flex;
        flex-wrap: wrap;
        align-items: baseline;
    `,
    input: css`
        margin: 0 0.5em;
    `,
    probability: css`
        width: 5rem;
    `,
    year: css`
        width: 85px;
    `,
};

const TargetsPopulationLevelSpecification = (props) => (
    <div className={classes.container}>
        <span>What target of</span>

        <NativeSelect
            value={props.delta}
            className={classes.input}
            onChange={(e) => {
                props.onSelectDelta(e.target.value);
            }}
        >
            <option value="total">total</option>
            <option value="additional">additional</option>
        </NativeSelect>

        {props.delta === "additional" && (
            <>
                <span>(relative to the year</span>

                <TextField
                    type="number"
                    size="small"
                    margin="dense"
                    inputProps={{
                        min: minimumYear,
                        max: maximumYear,
                    }}
                    value={props.relativeToYear || ""}
                    className={classNames(classes.input, classes.year)}
                    onChange={(e) => {
                        props.onChangeRelativeToYear(parseInt(e.target.value, 10) || undefined);
                    }}
                    onBlur={(e) => {
                        props.onChangeRelativeToYear(parseInt(e.target.value, 10) || undefined, true);
                    }}
                />
            </>
        )}

        <span>{`${props.delta === "additional" ? ") " : ""}number of`}</span>

        <MaritalStatusSelector
            value={props.maritalStatus}
            className={classes.input}
            onChange={props.onSelectMaritalStatus}
        />

        <span>women</span>

        <NativeSelect
            value={props.contraceptiveMethod}
            className={classes.input}
            onChange={(e) => {
                props.onSelectContraceptiveMethod(e.target.value);
            }}
        >
            {props.contraceptiveMethods.map((indicator) => (
                <option
                    key={indicator.value}
                    value={indicator.value}
                >
                    {indicator.label}
                </option>
            ))}
        </NativeSelect>

        <span>in the year</span>

        <TextField
            type="number"
            size="small"
            margin="dense"
            inputProps={{
                min: minimumYear,
                max: maximumYear,
            }}
            value={props.year || ""}
            className={classNames(classes.input, classes.year)}
            onChange={(e) => {
                props.onChangeYear(parseInt(e.target.value, 10) || undefined);
            }}
            onBlur={(e) => {
                props.onChangeYear(parseInt(e.target.value, 10) || undefined, true);
            }}
        />

        <span>corresponds to an attainment probability of</span>

        <TextField
            value={props.probability || ""}
            size="small"
            margin="dense"
            className={classNames(classes.input, classes.probability)}
            onChange={(e) => {
                props.onChangeProbability(e.target.value || undefined);
            }}
        />

        <span>where attainment probability refers to the probability of an outcome</span>

        <NativeSelect
            value={props.direction}
            className={classes.input}
            onChange={(e) => {
                props.onSelectDirection(e.target.value);
            }}
        >
            <option value="greaterThan">greater</option>
            <option value="lessThan">smaller</option>
        </NativeSelect>

        <span>than the target value?</span>
    </div>
);

TargetsPopulationLevelSpecification.propTypes = {
    contraceptiveMethods: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    delta: deltaPropType,
    relativeToYear: PropTypes.number,
    maritalStatus: maritalStatusPropType,
    contraceptiveMethod: PropTypes.string.isRequired,
    year: PropTypes.number,
    probability: PropTypes.string,
    direction: directionPropType,
    level: PropTypes.number,
    onSelectDelta: PropTypes.func.isRequired,
    onSelectMaritalStatus: PropTypes.func.isRequired,
    onChangeRelativeToYear: PropTypes.func.isRequired,
    onSelectContraceptiveMethod: PropTypes.func.isRequired,
    onChangeYear: PropTypes.func.isRequired,
    onChangeProbability: PropTypes.func.isRequired,
    onSelectDirection: PropTypes.func.isRequired,
};

export default TargetsPopulationLevelSpecification;
